<template>
  <b-modal
    v-model="showModal"
    id="question-form-modal"
    :title="editMode ? 'Edit question' : 'Add question'"
    size="lg"
    :ok-title="loading ? '' : editMode ? 'Edit question' : 'Create question'"
    cancel-title="Cancel"
    ok-variant="success"
    :cancel-disabled="loading"
    @hidden="$emit('hidden')"
    @ok="handleSubmit">
    <template #modal-ok>
      <div class="d-flex align-items-center" style="min-width: 108px; justify-content: center">
        <b-spinner small v-if="loading"></b-spinner>
        <span v-else>{{ editMode ? 'Edit question' : 'Create question' }}</span>
      </div>
    </template>

    <!-- ERRORS -->
    <b-alert v-if="Object.keys(errors).length > 0" show variant="danger">
      <div class="h5">Errors occurred:</div>
      <ul>
        <li v-for="(error, field) in errors" :key="field">
          <!-- {{ field }}: {{ error.msg }} -->
          {{ error.msg }}
        </li>
      </ul>
    </b-alert>

    <form ref="form" @submit.prevent class="b-form">
      <b-row>
        <b-col cols="12" md="6">
          <!-- Type -->
          <b-form-group
            label="Type *"
            label-for="type-input"
            :state="form.type ? true : null"
            invalid-feedback="Type is required">
            <b-form-select
              id="type-input"
              v-model="form.type"
              :options="['Blindtest', 'Classique', 'Juste Prix', { value: 'Binaire', text: 'True or False' }]"
              required
              :state="form.type ? true : null"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <!-- Rubrique -->
          <b-form-group label="Topic" label-for="rubrique-input">
            <vue-typeahead-bootstrap
              ref="typeaheadTheme"
              inputName="theme"
              :showOnFocus="true"
              placeholder="Choose a topic (use | for multiple)"
              :data="themesList"
              :maxMatches="1000"
              v-model="form.theme" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <!-- Langue -->
          <b-form-group label="Language" label-for="language-input">
            <b-form-select id="language-input" v-model="form.language" :options="languageOptions"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6"></b-col>
      </b-row>

      <!-- Question -->
      <b-form-group
        label="Question *"
        label-for="question-input"
        :state="form.question ? true : null"
        invalid-feedback="Question is required">
        <b-form-input
          id="question-input"
          v-model="form.question"
          placeholder="Enter the question"
          required
          :state="form.question ? true : null"></b-form-input>
      </b-form-group>

      <!-- Bonne réponse -->
      <b-form-group
        label="Correct answer *"
        label-for="reponse-ok-input"
        :state="form.response_ok ? true : null"
        invalid-feedback="Correct answer is required">
        <template v-if="form.type === 'Binaire'">
          <b-form-select
            id="reponse-ok-input"
            v-model="form.boolean"
            :options="[
              { value: 'true', text: 'True' },
              { value: 'false', text: 'False' },
            ]"
            required
            :state="form.boolean ? true : null"></b-form-select>
        </template>
        <template v-else-if="hasApproxAnwser">
          <b-form-input
            id="reponse-ok-input"
            v-model="form.response_ok"
            type="number"
            step="0.01"
            placeholder="Enter the correct answer to the question"
            required
            :state="form.response_ok ? true : null"></b-form-input>
        </template>
        <template v-else>
          <b-form-input
            id="reponse-ok-input"
            v-model="form.response_ok"
            type="text"
            placeholder="Enter the correct answer to the question"
            required
            :state="form.response_ok ? true : null"></b-form-input>
        </template>
      </b-form-group>

      <template v-if="hasApproxAnwser">
        <b-row>
          <b-col cols="12" md="4">
            <!-- Début -->
            <b-form-group
              label="Start *"
              label-for="debut-input"
              :state="form.response_approx_start ? true : null"
              invalid-feedback="Start is required">
              <b-form-input
                id="debut-input"
                v-model="form.response_approx_start"
                placeholder="Enter the start"
                required
                :state="form.response_approx_start ? true : null"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <!-- Interval -->
            <b-form-group
              label="Interval *"
              label-for="interval-input"
              :state="form.response_approx_stepper ? true : null"
              invalid-feedback="Interval is required">
              <b-form-input
                id="interval-input"
                v-model="form.response_approx_stepper"
                placeholder="Enter the interval"
                required
                :state="form.response_approx_stepper ? true : null"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <!-- Unité -->
            <b-form-group
              label="Unit *"
              label-for="unite-input"
              :state="form.response_approx_unit ? true : null"
              invalid-feedback="Unit is required">
              <b-form-input
                id="unite-input"
                v-model="form.response_approx_unit"
                placeholder="Enter the unit"
                :state="form.response_approx_unit ? true : null"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </template>

      <template v-if="hasFourAnwsers">
        <b-row>
          <b-col cols="12" md="4">
            <!-- Réponse 2 -->
            <b-form-group
              label="Answer 2 *"
              label-for="reponse2-input"
              :state="form.response_2 ? true : null"
              invalid-feedback="Answer 2 is required">
              <b-form-input
                id="reponse2-input"
                v-model="form.response_2"
                placeholder="Answer 2"
                required
                :state="form.response_2 ? true : null"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <!-- Réponse 3 -->
            <b-form-group
              label="Answer 3 *"
              label-for="reponse3-input"
              :state="form.response_3 ? true : null"
              invalid-feedback="Answer 3 is required">
              <b-form-input
                id="reponse3-input"
                v-model="form.response_3"
                placeholder="Answer 3"
                required
                :state="form.response_3 ? true : null"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <!-- Réponse 4 -->
            <b-form-group
              label="Answer 4 *"
              label-for="reponse4-input"
              :state="form.response_4 ? true : null"
              invalid-feedback="Answer 4 is required">
              <b-form-input
                id="reponse4-input"
                v-model="form.response_4"
                placeholder="Answer 4"
                required
                :state="form.response_4 ? true : null"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </template>

      <b-form-group label="Level" label-for="niveau-input">
        <div class="d-flex gap-4">
          <b-form-checkbox v-model="form.enfant" class="mr-3" switch>Children</b-form-checkbox>
          <b-form-checkbox v-model="form.familial" class="mr-3" switch>Family</b-form-checkbox>
          <b-form-checkbox v-model="form.normal" switch>Normal</b-form-checkbox>
        </div>
      </b-form-group>

      <template v-if="canUploadAudio">
        <!-- Audio -->
        <b-form-group label="Audio" label-for="audio-input">
          <!-- <b-form-file
            id="audio-input"
            v-model="form.audio"
            placeholder="Choisissez un fichier audio"
            accept="audio/*"
          ></b-form-file> -->

          <AudioSelector
            :initial-audio="{
              name: form.mp3_question,
              path: form.mp3_question_url,
            }"
            buttons-position="right"
            empty-container-action="import"
            upload-file-category="questions"
            :disabled="loading"
            :choose-button-disabled="true"
            @file-uploaded="onAudioUploaded($event)"
            @file-selected="onFileSelected($event, 'mp3_question', 'mp3_question_url')" />
        </b-form-group>
      </template>

      <template v-if="canUploadImage">
        <!-- Image -->
        <b-form-group label="Image" label-for="image-input">
          <!-- <b-form-file
            id="image-input"
            v-model="form.image"
            placeholder="Choisissez une image"
            accept="image/*"
          ></b-form-file> -->

          <ImageSelector
            :initial-image="{
              name: form.custom_image,
              path: form.custom_image_url,
            }"
            buttons-position="right"
            empty-container-action="import"
            upload-file-category="questions"
            :disabled="loading"
            :choose-button-disabled="true"
            @file-uploaded="onImageUploaded($event)"
            @file-selected="onFileSelected($event, 'custom_image', 'custom_image_url')" />
        </b-form-group>
      </template>
    </form>
  </b-modal>
</template>

<script>
  import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap'
  import { formatThemesForInput } from '@/utils/themeUtils'
  import AudioSelector from './file/AudioSelector.vue'
  import ImageSelector from './file/ImageSelector.vue'
  import { getLanguageSelectOptions } from '@/constants/languages'
  export default {
    name: 'QuestionForm',
    components: {
      VueTypeaheadBootstrap,
      AudioSelector,
      ImageSelector,
    },
    props: {
      themesList: {
        type: Array,
        default: () => [],
      },
      show: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      questionToEdit: {
        type: Object,
        default: null,
      },
      errors: {
        type: Object,
        default: () => {},
      },
      generalFilters: {
        type: Object,
        default: () => ({
          versions: [],
          language: 'fr',
        }),
      },
    },
    computed: {
      showModal: {
        get() {
          return this.show
        },
        set(value) {
          this.$emit('update:show', value)
        },
      },
      hasFourAnwsers() {
        return this.form.type === 'Classique' || this.form.type === 'Blindtest'
      },
      hasApproxAnwser() {
        return this.form.type === 'Juste Prix'
      },
      canUploadAudio() {
        return this.form.type === 'Classique' || this.form.type === 'Blindtest' || this.form.type === 'Juste Prix'
      },
      canUploadImage() {
        return this.form.type === 'Classique' || this.form.type === 'Blindtest' || this.form.type === 'Juste Prix'
      },
      editMode() {
        return !!this.questionToEdit
      },
      languageOptions() {
        const options = getLanguageSelectOptions()
        return Object.keys(options).map((code) => ({
          value: code,
          text: options[code],
        }))
      },
    },
    data() {
      return {
        form: {
          question: '',
          theme: '',
          type: 'Classique',
          language: this.generalFilters.language || 'fr',
          boolean: false,
          response_ok: '',
          response_2: '',
          response_3: '',
          response_4: '',
          response_approx_start: null,
          response_approx_stepper: null,
          response_approx_unit: null,
          enfant: false,
          familial: false,
          normal: false,
          mp3_question: null,
          mp3_question_url: null,
          custom_image: null,
          custom_image_url: null,
        },
      }
    },
    watch: {
      questionToEdit: {
        immediate: true,
        handler(question) {
          if (question) {
            this.form = {
              ...this.form,
              ...question,
              theme: formatThemesForInput(question.theme),
            }
          } else {
            // Réinitialiser le formulaire
            this.form = {
              question: '',
              theme: '',
              type: 'Classique',
              language: this.generalFilters.language || 'fr',
              boolean: false,
              response_ok: '',
              response_2: '',
              response_3: '',
              response_4: '',
              response_approx_start: null,
              response_approx_stepper: null,
              response_approx_unit: null,
              enfant: false,
              familial: false,
              normal: false,
              mp3_question: null,
              mp3_question_url: null,
              custom_image: null,
              custom_image_url: null,
            }
          }
        },
      },
      'generalFilters.language'(newLang) {
        // Mettre à jour la langue du formulaire uniquement si on n'est pas en mode édition
        if (!this.questionToEdit && newLang) {
          this.form.language = newLang
        }
      },
    },
    methods: {
      handleSubmit(bvModalEvent) {
        bvModalEvent.preventDefault()

        // Déclenche la validation native du formulaire
        const form = this.$refs.form
        if (!form.reportValidity()) {
          bvModalEvent.preventDefault()
          return
        }

        // Validation spécifique selon le type
        if (this.form.type === 'Juste Prix') {
          if (
            !this.form.response_approx_start ||
            !this.form.response_approx_stepper ||
            !this.form.response_approx_unit
          ) {
            bvModalEvent.preventDefault()
            return
          }
        } else if (!['Binaire', 'Juste Prix'].includes(this.form.type)) {
          if (!this.form.response_2 || !this.form.response_3 || !this.form.response_4) {
            bvModalEvent.preventDefault()
            return
          }
        }

        // Si tout est valide, émet l'événement submit
        this.$emit('submit', this.form)
      },
      onAudioUploaded(file) {
        this.$emit('audioUploaded', file)
      },
      onImageUploaded(file) {
        this.$emit('imageUploaded', file)
      },
      onFileSelected(file, fieldname = null, fieldpath = null) {
        if (!file) {
          if (fieldname) this.$set(this.form, fieldname, null)
          if (fieldpath) this.$set(this.form, fieldpath, null)
          return
        }

        console.log('file', file)
        console.log('fieldname', fieldname)
        console.log('fieldpath', fieldpath)

        if (fieldname) this.$set(this.form, fieldname, file.name)
        if (fieldpath) this.$set(this.form, fieldpath, file.url || file.path)
      },
    },
  }
</script>
